import { useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import type { AdminUpdateVatRequest } from '../types/admin-garage-edit';
import TypedConfirmDialog from './TypedConfirmDialog';

interface EditGarageVatDialogProps {
  garageId: string;
  current: { vatRegistered?: boolean | null; trn?: string | null; vatRegisteredFrom?: string | null };
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Admin edit of the garage's UAE VAT registration. The TRN drives the customer
 * invoice type (Tax Invoice vs plain Invoice) and settlement documents, so the
 * save goes through the same typed-confirm gate as bank details.
 */
export default function EditGarageVatDialog({ garageId, current, onClose, onSaved }: EditGarageVatDialogProps) {
  const { token } = useAuth();
  const [form, setForm] = useState<AdminUpdateVatRequest>({
    vatRegistered: current.vatRegistered ?? false,
    trn: current.trn ?? '',
    vatRegisteredFrom: current.vatRegisteredFrom ?? '',
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof AdminUpdateVatRequest>(k: K, v: AdminUpdateVatRequest[K]) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError(null);
  };

  const performSave = async () => {
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      await api.adminGarageUpdateVat(garageId, {
        vatRegistered: form.vatRegistered,
        trn: form.vatRegistered ? (form.trn || null) : null,
        vatRegisteredFrom: form.vatRegistered ? (form.vatRegisteredFrom || null) : null,
      }, token);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update VAT registration');
      setShowConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  if (showConfirm) {
    return (
      <TypedConfirmDialog
        title="Confirm VAT registration edit"
        message="You are about to change the garage's VAT registration. This directly affects how customer invoices are issued (Tax Invoice with VAT extraction vs plain Invoice) and settlement documents. Applies immediately."
        confirmPhrase="EDIT VAT"
        confirmLabel="Apply changes"
        danger
        onConfirm={performSave}
        onCancel={() => setShowConfirm(false)}
      />
    );
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowConfirm(true);
  };

  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true">
      <form className="admin-modal" onSubmit={onSubmit}>
        <div className="admin-modal-header"><h3>Edit VAT Registration</h3></div>
        <div className="admin-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.vatRegistered}
              onChange={(e) => set('vatRegistered', e.target.checked)}
            />
            Garage is VAT-registered
          </label>
          {form.vatRegistered && (
            <>
              <label>TRN (15 digits)
                <input
                  className="admin-input"
                  required
                  pattern="^\d{15}$"
                  title="UAE TRN is exactly 15 digits"
                  value={form.trn ?? ''}
                  onChange={(e) => set('trn', e.target.value)}
                />
              </label>
              <label>Registered from
                <input
                  className="admin-input"
                  type="date"
                  value={form.vatRegisteredFrom ?? ''}
                  onChange={(e) => set('vatRegisteredFrom', e.target.value)}
                />
              </label>
            </>
          )}
          {error && <div className="admin-error">{error}</div>}
        </div>
        <div className="admin-modal-footer">
          <button type="button" className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="admin-btn admin-btn-danger" disabled={saving}>{saving ? 'Saving…' : 'Continue'}</button>
        </div>
      </form>
    </div>
  );
}
